import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BottomNavbar from "../components/BottomNavbar";
import { ajukanJurnalMingguan, updateJurnalMingguan } from "../services/jurnalMingguanService";
import { prepareJournalPhoto } from '../services/journalPhotoPreparer';
import { ApiConfig } from "../constants/apiConfig";
import logo from "../assets/EPRAJOGO1.png";

function InputJurnalMingguan() {
  const navigate = useNavigate();
  const location = useLocation();
  const journal = location.state?.journal;
  const isDraft = !journal || journal.status_verifikasi === 'draft';
  const submitting = useRef(false);
  const processingPhotos = useRef(false);
  const previewUrls = useRef(new Set());
  const mounted = useRef(true);
  const siswaId = localStorage.getItem("user_id");

  const [mingguKe, setMingguKe] = useState(journal?.minggu_ke?.toString() || "");
  const [namaPekerjaan, setNamaPekerjaan] = useState(journal?.nama_pekerjaan || "");
  const [perencanaan, setPerencanaan] = useState(journal?.perencanaan_kegiatan || "");
  const [fotos, setFotos] = useState(() => (journal?.foto || []).map((photo) => ({
    id: photo.id,
    file: null,
    file_path: photo.file_path,
    url: photo.url,
    deskripsi: photo.deskripsi || "",
    removed: false,
  })));
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    mounted.current = true;
    const urls = previewUrls.current;
    return () => {
      mounted.current = false;
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const handleFotoChange = async (e) => {
    const input = e.target;
    if (processingPhotos.current || submitting.current) return;
    const files = Array.from(input.files || []);
    const activeCount = fotos.filter((foto) => !foto.removed).length;
    if (activeCount + files.length > 4) {
      alert("Maksimal upload 4 foto.");
      input.value = "";
      return;
    }
    processingPhotos.current = true;
    setProcessing(true);
    try {
      const preparedFiles = await Promise.all(files.map(prepareJournalPhoto));
      if (!mounted.current) return;
      const newFotoObjects = preparedFiles.map((file) => {
        const url = URL.createObjectURL(file);
        previewUrls.current.add(url);
        return { file, url, deskripsi: "", removed: false };
      });
      setFotos((current) => [...current, ...newFotoObjects]);
    } catch (error) {
      if (mounted.current) alert(error.message || "Gagal memproses foto.");
    } finally {
      input.value = "";
      processingPhotos.current = false;
      if (mounted.current) setProcessing(false);
    }
  };

  const updateDeskripsi = (index, value) => {
    setFotos((current) => current.map((foto, itemIndex) => itemIndex === index ? { ...foto, deskripsi: value } : foto));
  };

  const removeFoto = (index) => {
    if (fotos[index].file) {
      URL.revokeObjectURL(fotos[index].url);
      previewUrls.current.delete(fotos[index].url);
    }
    setFotos((current) => current.flatMap((foto, itemIndex) => {
      if (itemIndex !== index) return [foto];
      return foto.id ? [{ ...foto, removed: true }] : [];
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting.current || processingPhotos.current) return;
    if (!siswaId) return alert('Sesi berakhir. Login kembali.');
    const activePhotos = fotos.filter((foto) => !foto.removed);
    if (!isDraft && (!mingguKe || !namaPekerjaan.trim() || !perencanaan.trim() || activePhotos.length === 0)) {
      alert("Harap lengkapi semua isian.");
      return;
    }
    if (activePhotos.length > 4) {
      alert("Maksimal upload 4 foto.");
      return;
    }

    const formData = new FormData();
    formData.append("siswa_id", siswaId);
    formData.append("minggu_ke", mingguKe);
    formData.append("nama_pekerjaan", namaPekerjaan);
    formData.append("perencanaan_kegiatan", perencanaan);

    fotos.forEach((foto, index) => {
      if (foto.id) {
        formData.append(`foto[${index}][id]`, foto.id);
        if (foto.removed) {
          formData.append(`foto[${index}][delete]`, "true");
          return;
        }
      }
      if (foto.file) formData.append(`foto[${index}][file]`, foto.file);
      formData.append(`foto[${index}][deskripsi]`, foto.deskripsi || `Foto ${index + 1}`);
    });

    submitting.current = true;
    setLoading(true);
    try {
      const result = journal
        ? await updateJurnalMingguan(journal.id, formData)
        : await ajukanJurnalMingguan(formData);
      if (result.success) {
        alert(result.message || (isDraft ? 'Draft disimpan. Kirim ke guru dari daftar jurnal setelah lengkap.' : "Jurnal diperbaiki dan dikirim ulang untuk verifikasi."));
        navigate("/jurnal-mingguan");
      } else {
        alert(result.message || "Terjadi kesalahan saat menyimpan jurnal.");
      }
    } catch (error) {
      alert(error.message || 'Gagal menyimpan jurnal mingguan.');
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  return (
    <main className="app-mobile-page">
      <header className="app-brand-header">
        <div className="app-brand-row">
          <img src={logo} alt="Logo E-Prajogo" className="app-brand-logo" />
          <div><p className="app-brand-name">E-PRAJOGO</p><p className="app-brand-school">SMK NEGERI GUDO</p></div>
          <span className="ms-auto rounded-pill px-3 py-2 small fw-semibold" style={{ background: "#ffffff20", color: "#fff" }}>SISWA</span>
        </div>
        <h1 className="app-header-title">{journal ? (isDraft ? 'Edit Draft Mingguan' : "Perbaiki Jurnal Mingguan") : "Input Jurnal Mingguan"}</h1>
        <p className="app-header-subtitle">{isDraft ? 'Simpan dulu sebagai draft. Kirim ke guru setelah isian dan foto lengkap.' : 'Perbaikan akan dikirim ulang untuk verifikasi guru.'}</p>
      </header>

      <section className="app-content">
      <form className="journal-form-card" onSubmit={handleSubmit}>
        <div className="mb-3">
          <label htmlFor="journal-week" className="form-label fw-semibold">Pilih Minggu</label>
          <select
            id="journal-week"
            className="form-select"
            value={mingguKe}
            onChange={(e) => setMingguKe(e.target.value)}
            required={!isDraft}
          >
            <option value="">-- Pilih --</option>
            {[...Array(25)].map((_, i) => (
              <option key={i + 1} value={i + 1}>
                Minggu ke-{i + 1}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label htmlFor="weekly-work" className="form-label fw-semibold">Nama Pekerjaan</label>
          <textarea
            id="weekly-work"
            className="form-control"
            placeholder="Contoh: Membuat laporan harian..."
            rows={2}
            value={namaPekerjaan}
            onChange={(e) => setNamaPekerjaan(e.target.value)}
            required={!isDraft}
          />
        </div>

        <div className="mb-3">
          <label htmlFor="weekly-plan" className="form-label fw-semibold">Perencanaan Kegiatan</label>
          <textarea
            id="weekly-plan"
            className="form-control"
            placeholder="Contoh: Menyusun rencana kegiatan minggu berikutnya..."
            rows={3}
            value={perencanaan}
            onChange={(e) => setPerencanaan(e.target.value)}
            required={!isDraft}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-semibold">Upload Foto Pelaksanaan (maks 4)</label>
          <div className="d-flex flex-wrap gap-2">
            {fotos.map((foto, index) => foto.removed ? null : (
              <div key={index} style={{ width: 90 }}>
                <div className="position-relative" style={{ width: 90, height: 90 }}>
                  <img
                    src={foto.url || (foto.file_path?.startsWith("http") ? foto.file_path : `${ApiConfig.baseImageUrl.replace(/\/$/, "")}/${(foto.file_path || "").replace(/^\//, "")}`)}
                    alt="Preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: 8,
                      border: "1px solid #ccc",
                    }}
                  />
                  <button
                    type="button"
                    aria-label={`Hapus foto ${index + 1}`}
                    disabled={loading || processing}
                    className="btn-close position-absolute top-0 end-0"
                    style={{ backgroundColor: "white", fontSize: 10 }}
                    onClick={() => removeFoto(index)}
                  />
                </div>
                <input
                  type="text"
                  className="form-control mt-1"
                  placeholder={`Deskripsi foto ${index + 1}`}
                  aria-label={`Deskripsi foto ${index + 1}`}
                  maxLength={1000}
                  value={foto.deskripsi}
                  onChange={(e) => updateDeskripsi(index, e.target.value)}
                />
              </div>
            ))}
            {fotos.filter((foto) => !foto.removed).length < 4 && (
              <label
                htmlFor="foto-upload"
                className="d-flex justify-content-center align-items-center"
                style={{
                  width: 90,
                  height: 90,
                  border: "1px dashed #ccc",
                  borderRadius: 8,
                  cursor: "pointer",
                }}
              >
                <i className="bi bi-camera" style={{ fontSize: 24 }} aria-hidden="true"></i>
                <span className="visually-hidden">Tambah foto</span>
              </label>
            )}
            <input
              id="foto-upload"
              type="file"
              accept="image/*"
              disabled={loading || processing}
              multiple
              onChange={handleFotoChange}
              style={{ display: "none" }}
            />
          </div>
        </div>
        {processing && <p role="status" className="text-primary"><span className="spinner-border spinner-border-sm me-2" />Menyiapkan foto...</p>}

        <button
          className="btn btn-primary w-100 mt-4"
          type="submit"
          disabled={loading || processing}
          style={{ borderRadius: 12 }}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Menyimpan...
            </>
          ) : (
            <>
              <i className="bi bi-save me-2" />
              {isDraft ? "Simpan draft" : "Kirim Ulang untuk Verifikasi"}
            </>
          )}
        </button>
      </form>
      </section>
      <BottomNavbar />
    </main>
  );
}

export default InputJurnalMingguan;
